import type { AllowedMaterialSpecInfoAiModel, BuyerLoadDetail, EvidenceSessionAIModel, SiteMaterialDetail } from 'api';
import { useState } from 'react';
import {
  Box,
  Fade,
  LinearProgress,
  MediaDialog,
  type PhotoEvidenceItem,
  PhotoEvidenceList,
  PhotoFilePicker,
  Typography,
  useBreakpoint,
} from 'ui';

import {
  CameraDialog,
  type GuideProps,
  MaterialForm,
  type PreviewGalleryImage,
  useManagePreviews,
} from '../../../camera-guidance';
import { useBaleEvidence } from '../../hooks/useBaleEvidence/useBaleEvidence';

interface BaleEvidenceGalleryProps {
  onToggleCamera: () => void;
  isCameraOpen: boolean;
  filePickerRef: React.RefObject<HTMLInputElement>;
  siteMaterial?: SiteMaterialDetail;
  model?: AllowedMaterialSpecInfoAiModel;
  load?: BuyerLoadDetail;
  onUpload?: (model: string, photo: File) => void;
  guideIllustrations: GuideProps['guideIllustrations'];
}

export const BaleEvidenceGallery = ({
  onToggleCamera,
  isCameraOpen,
  filePickerRef,
  siteMaterial,
  model,
  load,
  onUpload,
  guideIllustrations,
}: BaleEvidenceGalleryProps) => {
  const { isMobile } = useBreakpoint();
  const [selectedImage, setSelectedImage] = useState<string>('');
  const { data, addBalePhoto, analyzingBales, carouselMedia, uploadProgress, allPhotos, removeBalePhoto } =
    useBaleEvidence({ loadId: load?.id, siteMaterialId: siteMaterial?.id });

  const { previewImages, addImage, updateOnlineImage, resetSelectedImages, handleDeletePreviewImage } =
    useManagePreviews();

  const handleCapture = async (photo: File) => {
    const addedImage = await addImage({
      isOnline: true,
      photo,
    });

    const result = await addBalePhoto({ model: String(model), photo });

    if (result && 'model_results' in result && result.model_results) {
      updateOnlineImage({
        accepted: !(result.model_results.outputs?.[0].image_rejected ?? true),
        aiModel: (model as EvidenceSessionAIModel) ?? undefined,
        annotations: [...(result.model_results.coco_prediction ?? [])],
        attachmentDetails: result.attachment_details ?? undefined,
        imageRejectionReasons: result.model_results.outputs?.[0]?.image_rejection_reasons ?? [],
        previewImage: addedImage,
        targetWeightPercentage: result.target_weight_percentage ?? undefined,
        url: typeof result.image === 'string' ? result.image : result.image.url,
      });
    }

    onUpload?.(String(model), photo);
  };

  const handleSelect = (photo: PhotoEvidenceItem) => {
    setSelectedImage(photo.src);
  };

  const handleCompletePreviewSession = () => {
    resetSelectedImages();
    onToggleCamera();
  };

  const deletePreviewImage = (image: PreviewGalleryImage) => {
    if (!data?.results.length) {
      return;
    }

    handleDeletePreviewImage({
      evidence: data.results,
      image,
      isOnline: true, // Temporary whilst we don't have offline support
      removeEvidenceFile: removeBalePhoto,
    });
  };

  return (
    <>
      {data?.results.length === 0 ? (
        <Typography>No evidence found</Typography>
      ) : (
        <PhotoEvidenceList
          slotProps={{
            imageList: {
              cols: isMobile ? 3 : 5,
              gap: 16,
            },
            resultIcon: {
              size: 'medium',
            },
          }}
          evidencePhotos={allPhotos ?? []}
          handleClick={handleSelect}
          openOnClick
        />
      )}

      <Fade in={analyzingBales.length > 0} timeout={300}>
        <Box marginTop={4}>
          <LinearProgress value={uploadProgress} />
          <Typography variant="body2" color="text.secondary" textAlign="center">
            Analysing {analyzingBales.length} photo{analyzingBales.length > 1 ? 's' : ''}...
          </Typography>
        </Box>
      </Fade>

      <CameraDialog
        isCameraOpen={isCameraOpen}
        toggle={onToggleCamera}
        cameraProps={{
          guideIllustrations,
          handleCapture,
          handleClose: onToggleCamera,
          handleDeletePreviewImage: deletePreviewImage,
          handleOnComplete: handleCompletePreviewSession,
          materialForm: MaterialForm.baled,
          previewImages,
        }}
      />

      <PhotoFilePicker
        ref={filePickerRef}
        onSelectFiles={(photos) => photos.forEach((photo) => handleCapture(photo))}
        hide={true}
        multiple={true}
      />

      <MediaDialog
        open={Boolean(selectedImage)}
        selectedMedium={selectedImage}
        onClose={() => setSelectedImage('')}
        media={carouselMedia}
        handleNavigate={setSelectedImage}
      />
    </>
  );
};
